<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  PLATFORMS,
  SOCIAL_CHANNEL_TYPE,
  SOCIAL_ENTITY_I18N,
  getInboxPlatform,
  isBotPlatform,
} from 'dashboard/helper/sourcePlatforms';
import { formatCount } from 'dashboard/components-next/Conversation/SourceCard/helpers';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const emit = defineEmits(['openBotTab']);

const { t, locale } = useI18n();
const store = useStore();

const ENTITY_ICONS = {
  comments: 'i-lucide-message-circle',
  direct: 'i-lucide-mail',
  mentions: 'i-lucide-at-sign',
  messages: 'i-lucide-users',
  feedbacks: 'i-lucide-star',
  questions: 'i-lucide-circle-help',
  returns: 'i-lucide-undo-2',
  dialogs: 'i-lucide-messages-square',
  reviews: 'i-lucide-map-pin',
};

const platformKey = computed(() => getInboxPlatform(props.inbox));
const platform = computed(() => PLATFORMS[platformKey.value]);
const isSocial = computed(
  () => props.inbox.channel_type === SOCIAL_CHANNEL_TYPE
);
const isBot = computed(() => isBotPlatform(platformKey.value));
const profile = computed(() => props.inbox.profile || {});

const options = computed(() => platform.value?.entities || []);
const selected = ref([]);
const isSaving = ref(false);

watch(
  () => props.inbox.entities,
  value => {
    selected.value = [...(value || [])];
  },
  { immediate: true }
);

const isDirty = computed(() => {
  const current = [...(props.inbox.entities || [])].sort().join(',');
  return current !== [...selected.value].sort().join(',');
});

const toggle = entity => {
  selected.value = selected.value.includes(entity)
    ? selected.value.filter(item => item !== entity)
    : [...selected.value, entity];
};

const entityHint = entity => {
  const key = `MULTICHAT.ENTITY_HINTS.${entity.toUpperCase()}`;
  return t(key) === key ? '' : t(key);
};

const isConnected = computed(
  () => (props.inbox.connection_status || 'connected') === 'connected'
);

const save = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: { entities: selected.value },
    });
    useAlert(t('MULTICHAT.INBOX_SETTINGS.SAVED'));
  } catch (error) {
    useAlert(error.message || t('MULTICHAT.INBOX_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div v-if="platform" class="flex flex-col gap-5">
    <div
      v-if="isSocial"
      class="flex items-center gap-4 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <div class="relative flex-shrink-0">
        <img
          v-if="profile.avatar_url"
          :src="profile.avatar_url"
          :alt="profile.title"
          class="object-cover rounded-full size-14 border border-n-weak"
        />
        <span
          v-else
          class="grid rounded-full size-14 place-items-center text-lg font-bold bg-[#E30611] text-white"
        >
          {{ (profile.title || inbox.name || '?').slice(0, 1) }}
        </span>
        <span
          class="absolute -bottom-0.5 ltr:-right-0.5 rtl:-left-0.5 grid size-6 rounded-full place-items-center ring-2 ring-n-solid-1"
          :class="platform.badgeClass"
        >
          <span class="size-3.5" :class="platform.icon" />
        </span>
      </div>
      <div class="flex flex-col flex-1 min-w-0 gap-0.5">
        <span class="text-base font-semibold text-n-slate-12 truncate">
          {{ profile.title || inbox.name }}
        </span>
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <a
            v-if="profile.handle"
            :href="profile.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-n-blue-11 hover:underline"
          >
            {{ profile.handle }}
            <span class="i-lucide-external-link size-3" />
          </a>
          <span
            v-if="profile.followers"
            class="inline-flex items-center gap-1 text-n-slate-11"
          >
            <span class="i-lucide-users size-3.5" />
            {{
              t('MULTICHAT.INBOX_SETTINGS.FOLLOWERS', {
                count: formatCount(profile.followers, locale),
              })
            }}
          </span>
          <span class="text-n-slate-10">{{ t(platform.name) }}</span>
        </div>
      </div>
      <span
        class="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-semibold flex-shrink-0"
        :class="
          isConnected
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-ruby-3 text-n-ruby-11'
        "
      >
        <span
          class="rounded-full size-1.5"
          :class="isConnected ? 'bg-n-teal-9' : 'bg-n-ruby-9'"
        />
        {{
          isConnected
            ? t('INBOX_MGMT.MARKETPLACE_STATUS.CONNECTED')
            : t('INBOX_MGMT.MARKETPLACE_STATUS.ERROR')
        }}
      </span>
    </div>

    <div
      v-if="isBot"
      class="flex items-start gap-3 p-4 border rounded-xl border-n-iris-4 bg-n-iris-2"
    >
      <span
        class="grid flex-shrink-0 rounded-full size-9 place-items-center bg-gradient-to-br from-[#E30611] to-[#B0040D] text-white"
      >
        <span class="i-lucide-bot size-5" />
      </span>
      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <span class="text-sm font-semibold text-n-slate-12">
          {{ t('MULTICHAT.INBOX_SETTINGS.BOT_TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('MULTICHAT.INBOX_SETTINGS.BOT_DESCRIPTION') }}
        </span>
      </div>
      <Button
        :label="t('MULTICHAT.INBOX_SETTINGS.BOT_OPEN_TAB')"
        size="sm"
        variant="faded"
        color="slate"
        icon="i-lucide-arrow-right"
        trailing-icon
        @click="emit('openBotTab')"
      />
    </div>

    <div v-if="options.length" class="flex flex-col gap-3">
      <div class="flex flex-col gap-1">
        <span class="text-heading-3 text-n-slate-12">
          {{ t('MULTICHAT.INBOX_SETTINGS.COLLECT_TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('MULTICHAT.INBOX_SETTINGS.COLLECT_DESCRIPTION') }}
        </span>
      </div>
      <div class="grid gap-2 sm:grid-cols-2">
        <button
          v-for="entity in options"
          :key="entity"
          type="button"
          class="flex items-start gap-3 p-3 text-start border rounded-xl transition-colors"
          :class="
            selected.includes(entity)
              ? 'border-n-brand bg-n-brand/5'
              : 'border-n-weak bg-n-solid-1 hover:bg-n-alpha-1'
          "
          @click="toggle(entity)"
        >
          <span
            class="grid flex-shrink-0 mt-0.5 rounded size-4 place-items-center border"
            :class="
              selected.includes(entity)
                ? 'bg-n-brand border-n-brand text-white'
                : 'border-n-strong'
            "
          >
            <span
              v-if="selected.includes(entity)"
              class="i-lucide-check size-3"
            />
          </span>
          <span class="flex flex-col min-w-0 gap-0.5">
            <span
              class="inline-flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
            >
              <span
                class="size-4 text-n-slate-10"
                :class="ENTITY_ICONS[entity]"
              />
              {{ t(SOCIAL_ENTITY_I18N[entity]) }}
            </span>
            <span v-if="entityHint(entity)" class="text-xs text-n-slate-11">
              {{ entityHint(entity) }}
            </span>
          </span>
        </button>
      </div>
      <div class="flex items-center gap-3">
        <Button
          :label="t('MULTICHAT.INBOX_SETTINGS.SAVE')"
          size="sm"
          :is-loading="isSaving"
          :disabled="!isDirty || isSaving"
          @click="save"
        />
        <span v-if="!selected.length" class="text-xs text-n-amber-11">
          {{ t('MULTICHAT.INBOX_SETTINGS.NOTHING_SELECTED') }}
        </span>
      </div>
    </div>
  </div>
</template>
